import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, TrendingDown, Calendar, Award, Target } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import ProgressChart from '../components/ProgressChart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from 'recharts';
import { getTodayString, formatDateBR, formatDateShortBR, parseLocalDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Progresso = () => {
  const [progress, setProgress] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('semanal'); // semanal, mensal, semestral
  const [formData, setFormData] = useState({
    data: getTodayString(),
    peso: '',
    humor: '',
    agua_ml: '',
    atividade_fisica: '',
    horas_sono: '',
    // Medidas do dia
    cintura: '',
    quadril: '',
    peito: '',
    braco: '',
    perna: ''
  });
  const [loading, setLoading] = useState(true);
  const [showMeasurements, setShowMeasurements] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [progressRes, measurementsRes] = await Promise.all([
        axios.get(`${API_URL}/api/progress`),
        axios.get(`${API_URL}/api/measurements`)
      ]);
      setProgress(progressRes.data);
      setMeasurements(measurementsRes.data);
    } catch (error) {
      console.error('Erro ao carregar progresso:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitProgress = async (e) => {
    e.preventDefault();
    try {
      // Salvar progresso diário
      const progressData = {
        data: formData.data,
        peso: formData.peso ? parseFloat(formData.peso) : null,
        humor: formData.humor || null,
        agua_ml: formData.agua_ml ? parseInt(formData.agua_ml) : null,
        atividade_fisica: formData.atividade_fisica || null,
        horas_sono: formData.horas_sono ? parseFloat(formData.horas_sono) : null
      };
      await axios.post(`${API_URL}/api/progress/daily`, progressData);
      
      // Se tiver medidas, salvar também
      const hasMeasurements = formData.cintura || formData.quadril || formData.peito || formData.braco || formData.perna;
      if (hasMeasurements) {
        const measurementData = {
          data: formData.data,
          cintura: formData.cintura ? parseFloat(formData.cintura) : null,
          quadril: formData.quadril ? parseFloat(formData.quadril) : null,
          peito: formData.peito ? parseFloat(formData.peito) : null,
          braco: formData.braco ? parseFloat(formData.braco) : null,
          perna: formData.perna ? parseFloat(formData.perna) : null
        };
        await axios.post(`${API_URL}/api/measurements`, measurementData);
      }
      
      toast.success('Progresso registrado!');
      setShowForm(false);
      setShowMeasurements(false);
      setFormData({
        data: getTodayString(),
        peso: '',
        humor: '',
        agua_ml: '',
        atividade_fisica: '',
        horas_sono: '',
        cintura: '',
        quadril: '',
        peito: '',
        braco: '',
        perna: ''
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar progresso');
    }
  };

  // Filtrar dados por período
  const getFilteredData = () => {
    const now = new Date();
    let daysAgo = 7;
    
    if (selectedPeriod === 'mensal') {
      daysAgo = 30;
    } else if (selectedPeriod === 'semestral') {
      daysAgo = 180;
    }
    
    const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
    
    return progress
      .filter(p => {
        if (!p.peso) return false;
        const date = parseLocalDate(p.data);
        return date && date >= cutoffDate;
      })
      .sort((a, b) => {
        const dateA = parseLocalDate(a.data);
        const dateB = parseLocalDate(b.data);
        return dateA - dateB;
      })
      .map(p => ({
        data: formatDateShortBR(p.data),
        peso: p.peso,
        agua_ml: p.agua_ml,
        horas_sono: p.horas_sono
      }));
  };

  // Calcular estatísticas do período
  const calculateStats = () => {
    const filteredData = getFilteredData();
    if (filteredData.length === 0) return null;

    const pesos = filteredData.map(d => d.peso);
    const pesoInicial = pesos[0];
    const pesoFinal = pesos[pesos.length - 1];
    const perdaPeso = pesoInicial - pesoFinal;
    const mediaPeso = pesos.reduce((a, b) => a + b, 0) / pesos.length;
    
    // Calcular água média
    const aguaData = filteredData.filter(d => d.agua_ml);
    const mediaAgua = aguaData.length > 0 
      ? aguaData.reduce((a, b) => a + (b.agua_ml || 0), 0) / aguaData.length 
      : 0;
    
    // Calcular sono médio
    const sonoData = filteredData.filter(d => d.horas_sono);
    const mediaSono = sonoData.length > 0 
      ? sonoData.reduce((a, b) => a + (b.horas_sono || 0), 0) / sonoData.length 
      : 0;

    return {
      pesoInicial: pesoInicial.toFixed(1),
      pesoFinal: pesoFinal.toFixed(1),
      perdaPeso: perdaPeso.toFixed(1),
      mediaPeso: mediaPeso.toFixed(1),
      mediaAgua: Math.round(mediaAgua),
      mediaSono: mediaSono.toFixed(1),
      diasRegistrados: filteredData.length
    };
  };

  const pesoData = getFilteredData();
  const stats = calculateStats();

  const getPeriodLabel = () => {
    switch(selectedPeriod) {
      case 'semanal': return 'Progresso Semanal';
      case 'mensal': return 'Progresso Mensal';
      case 'semestral': return 'Progresso Semestral';
      default: return 'Progresso';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold mb-2 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Meu Progresso
            </h2>
            <p className="text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Acompanhe sua evolução ao longo do tempo
            </p>
          </div>
          <button
            data-testid="add-progress-button"
            onClick={() => setShowForm(!showForm)}
            className="bg-[#0F2B4C] text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-shadow"
          >
            <Plus size={24} />
          </button>
        </div>

        {/* Seletor de Período */}
        <div className="flex gap-3 overflow-x-auto pb-2">
          <button
            data-testid="period-semanal"
            onClick={() => setSelectedPeriod('semanal')}
            className={`px-6 py-3 rounded-full font-semibold whitespace-nowrap transition-all ${
              selectedPeriod === 'semanal'
                ? 'bg-[#B8977A] text-white shadow-lg'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Calendar className="inline mr-2" size={18} />
            Semanal
          </button>
          <button
            data-testid="period-mensal"
            onClick={() => setSelectedPeriod('mensal')}
            className={`px-6 py-3 rounded-full font-semibold whitespace-nowrap transition-all ${
              selectedPeriod === 'mensal'
                ? 'bg-[#B8977A] text-white shadow-lg'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Calendar className="inline mr-2" size={18} />
            Mensal
          </button>
          <button
            data-testid="period-semestral"
            onClick={() => setSelectedPeriod('semestral')}
            className={`px-6 py-3 rounded-full font-semibold whitespace-nowrap transition-all ${
              selectedPeriod === 'semestral'
                ? 'bg-[#B8977A] text-white shadow-lg'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Calendar className="inline mr-2" size={18} />
            Semestral
          </button>
        </div>

        {showForm && (
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Registrar Progresso Diário
            </h3>
            <form onSubmit={handleSubmitProgress} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Data</label>
                <input
                  data-testid="input-data"
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Peso (kg)</label>
                  <input
                    data-testid="input-peso"
                    type="number"
                    step="0.1"
                    value={formData.peso}
                    onChange={(e) => setFormData({ ...formData, peso: e.target.value })}
                    className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                    placeholder="70.5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Água (ml)</label>
                  <input
                    data-testid="input-agua"
                    type="number"
                    value={formData.agua_ml}
                    onChange={(e) => setFormData({ ...formData, agua_ml: e.target.value })}
                    className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                    placeholder="2000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Humor</label>
                <select
                  data-testid="select-humor"
                  value={formData.humor}
                  onChange={(e) => setFormData({ ...formData, humor: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                >
                  <option value="">Selecione...</option>
                  <option value="excelente">Excelente</option>
                  <option value="bom">Bom</option>
                  <option value="regular">Regular</option>
                  <option value="ruim">Ruim</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Atividade Física</label>
                <input
                  data-testid="input-atividade"
                  type="text"
                  value={formData.atividade_fisica}
                  onChange={(e) => setFormData({ ...formData, atividade_fisica: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                  placeholder="Caminhada 30min"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Horas de Sono</label>
                <input
                  data-testid="input-sono"
                  type="number"
                  step="0.5"
                  value={formData.horas_sono}
                  onChange={(e) => setFormData({ ...formData, horas_sono: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                  placeholder="8"
                />
              </div>

              {/* Toggle para Medidas */}
              <div className="border-t border-border pt-4">
                <button
                  type="button"
                  onClick={() => setShowMeasurements(!showMeasurements)}
                  className="w-full flex items-center justify-between p-4 bg-[#B8977A]/10 rounded-xl hover:bg-[#B8977A]/20 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-[#B8977A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span className="font-medium text-[#B8977A]">Registrar Medidas do Dia (opcional)</span>
                  </div>
                  <svg className={`w-5 h-5 text-[#B8977A] transition-transform ${showMeasurements ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showMeasurements && (
                  <div className="mt-4 p-4 bg-[#B8977A]/5 rounded-xl border border-[#B8977A]/20">
                    <p className="text-xs text-muted-foreground mb-3">Medidas em centímetros (cm)</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1 text-muted-foreground">Cintura</label>
                        <input
                          data-testid="input-cintura"
                          type="number"
                          step="0.1"
                          value={formData.cintura}
                          onChange={(e) => setFormData({ ...formData, cintura: e.target.value })}
                          className="w-full bg-white border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 px-3 text-sm"
                          placeholder="90"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-muted-foreground">Quadril</label>
                        <input
                          data-testid="input-quadril"
                          type="number"
                          step="0.1"
                          value={formData.quadril}
                          onChange={(e) => setFormData({ ...formData, quadril: e.target.value })}
                          className="w-full bg-white border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 px-3 text-sm"
                          placeholder="100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-muted-foreground">Peito</label>
                        <input
                          data-testid="input-peito"
                          type="number"
                          step="0.1"
                          value={formData.peito}
                          onChange={(e) => setFormData({ ...formData, peito: e.target.value })}
                          className="w-full bg-white border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 px-3 text-sm"
                          placeholder="95"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-muted-foreground">Braço</label>
                        <input
                          data-testid="input-braco"
                          type="number"
                          step="0.1"
                          value={formData.braco}
                          onChange={(e) => setFormData({ ...formData, braco: e.target.value })}
                          className="w-full bg-white border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 px-3 text-sm"
                          placeholder="35"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1 text-muted-foreground">Perna</label>
                        <input
                          data-testid="input-perna"
                          type="number"
                          step="0.1"
                          value={formData.perna}
                          onChange={(e) => setFormData({ ...formData, perna: e.target.value })}
                          className="w-full bg-white border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 px-3 text-sm"
                          placeholder="60"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  data-testid="submit-progress"
                  type="submit"
                  className="flex-1 bg-[#0F2B4C] text-white rounded-full py-3 font-bold hover:shadow-lg transition-shadow"
                >
                  Salvar
                </button>
                <button
                  data-testid="cancel-progress"
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 bg-muted text-muted-foreground rounded-full py-3 font-bold"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Carregando...</div>
        ) : (
          <>
            {/* Estatísticas do Período */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="text-[#B8977A]" size={20} />
                    <p className="text-sm text-muted-foreground">Perda de Peso</p>
                  </div>
                  <p className="text-3xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {stats.perdaPeso}
                  </p>
                  <p className="text-xs text-muted-foreground">kg no período</p>
                </div>

                <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="text-[#0F2B4C]" size={20} />
                    <p className="text-sm text-muted-foreground">Peso Médio</p>
                  </div>
                  <p className="text-3xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {stats.mediaPeso}
                  </p>
                  <p className="text-xs text-muted-foreground">kg</p>
                </div>

                <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="text-[#B8977A]" size={20} />
                    <p className="text-sm text-muted-foreground">Água Média</p>
                  </div>
                  <p className="text-3xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {stats.mediaAgua}
                  </p>
                  <p className="text-xs text-muted-foreground">ml/dia</p>
                </div>

                <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="text-[#0F2B4C]" size={20} />
                    <p className="text-sm text-muted-foreground">Dias Registrados</p>
                  </div>
                  <p className="text-3xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {stats.diasRegistrados}
                  </p>
                  <p className="text-xs text-muted-foreground">registros</p>
                </div>
              </div>
            )}

            {/* Gráfico Principal */}
            {pesoData.length > 0 ? (
              <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
                <h3 className="text-xl font-bold mb-6 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {getPeriodLabel()}
                </h3>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={pesoData}>
                    <defs>
                      <linearGradient id="colorPeso" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#B8977A" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#B8977A" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis 
                      dataKey="data" 
                      stroke="#737373"
                      style={{ fontSize: '12px', fontFamily: 'IBM Plex Sans, sans-serif' }}
                    />
                    <YAxis 
                      stroke="#737373"
                      style={{ fontSize: '12px', fontFamily: 'IBM Plex Sans, sans-serif' }}
                      domain={['dataMin - 2', 'dataMax + 2']}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#FCFCFC',
                        border: '1px solid #E5E5E5',
                        borderRadius: '12px',
                        fontFamily: 'IBM Plex Sans, sans-serif'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="peso" 
                      stroke="#B8977A" 
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#colorPeso)"
                      dot={{ fill: '#B8977A', r: 5 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="bg-card rounded-2xl shadow-sm border border-border p-12 text-center">
                <TrendingDown className="mx-auto text-muted-foreground mb-4" size={64} />
                <h3 className="text-xl font-bold mb-2 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Nenhum registro no período
                </h3>
                <p className="text-muted-foreground mb-4">
                  Comece a registrar seu progresso diário para ver seus resultados
                </p>
                <button
                  onClick={() => setShowForm(true)}
                  className="bg-[#0F2B4C] text-white rounded-full px-8 py-3 font-bold hover:shadow-lg transition-shadow"
                >
                  Adicionar Registro
                </button>
              </div>
            )}

            {/* Gráficos Secundários */}
            {pesoData.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Gráfico de Água */}
                {pesoData.some(d => d.agua_ml) && (
                  <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
                    <h3 className="text-lg font-semibold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Consumo de Água
                    </h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={pesoData.filter(d => d.agua_ml)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                        <XAxis 
                          dataKey="data" 
                          stroke="#737373"
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis 
                          stroke="#737373"
                          style={{ fontSize: '12px' }}
                        />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#FCFCFC',
                            border: '1px solid #E5E5E5',
                            borderRadius: '12px'
                          }}
                        />
                        <Bar 
                          dataKey="agua_ml" 
                          fill="#0F2B4C" 
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Gráfico de Sono */}
                {pesoData.some(d => d.horas_sono) && (
                  <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
                    <h3 className="text-lg font-semibold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Qualidade do Sono
                    </h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={pesoData.filter(d => d.horas_sono)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                        <XAxis 
                          dataKey="data" 
                          stroke="#737373"
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis 
                          stroke="#737373"
                          style={{ fontSize: '12px' }}
                          domain={[0, 12]}
                        />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#FCFCFC',
                            border: '1px solid #E5E5E5',
                            borderRadius: '12px'
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="horas_sono" 
                          stroke="#B8977A" 
                          strokeWidth={3}
                          dot={{ fill: '#B8977A', r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {/* Histórico Recente */}
            <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Histórico Recente
              </h3>
              {progress.length === 0 ? (
                <div className="text-center py-8">
                  <TrendingDown className="mx-auto text-muted-foreground mb-4" size={48} />
                  <p className="text-muted-foreground">Nenhum registro ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {progress.slice(0, 10).map((item) => (
                    <div
                      key={item.id}
                      data-testid={`progress-item-${item.id}`}
                      className="p-4 bg-muted rounded-xl hover:bg-muted/80 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-highlight">{formatDateBR(item.data)}</span>
                        {item.peso && (
                          <span className="text-[#B8977A] font-bold text-lg">{item.peso} kg</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-muted-foreground">
                        {item.humor && <div>😊 Humor: {item.humor}</div>}
                        {item.agua_ml && <div>💧 Água: {item.agua_ml}ml</div>}
                        {item.atividade_fisica && <div>🏃 {item.atividade_fisica}</div>}
                        {item.horas_sono && <div>😴 Sono: {item.horas_sono}h</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Progresso;
